// CustomerAppointments.jsx
import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  Alert,
  Tooltip,
  Chip,
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ApiServices from "../../Services/ApiServices/ApiServices";

const BASE_URL = "http://127.0.0.1:8000";

const CustomerAppointments = () => {
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(false);

  // upload dialog state
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadAppointment, setUploadAppointment] = useState(null); // appointment object
  const [uploadServiceId, setUploadServiceId] = useState(""); // appointment_service id
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadIsBefore, setUploadIsBefore] = useState("true");
  const [uploadLoading, setUploadLoading] = useState(false);

  // snack
  const [snack, setSnack] = useState({ open: false, message: "", severity: "success" });

  // read logged-in customer from sessionStorage 'user'
  const getSessionUser = () => {
    try {
      const raw = sessionStorage.getItem("user");
      if (!raw) return null;
      return JSON.parse(raw);
    } catch {
      return null;
    }
  };
  const user = getSessionUser();
  const userId = user?.id ?? null;

  // fetch all appointments and filter to logged-in customer
  const fetchAppointments = async () => {
    if (!userId) {
      setSnack({ open: true, message: "No logged-in user found in sessionStorage ('user')", severity: "error" });
      return;
    }
    setLoading(true);
    try {
      const res = await ApiServices.get("service-appointments/");
      const data = res?.data ?? res;
      if (!Array.isArray(data)) {
        setAppointments([]);
        setSnack({ open: true, message: "Unexpected data from server", severity: "error" });
      } else {
        // filter to logged-in customer
        const filtered = data
          .filter((a) => {
            if (!a.customer) return false;
            // some backends might return customer as id or object; handle object case:
            if (typeof a.customer === "object") return Number(a.customer.id) === Number(userId);
            return Number(a.customer) === Number(userId);
          })
          .map((a) => ({ ...a, _scheduled_at_date: a.scheduled_at ? new Date(a.scheduled_at) : null }));
        // sort newest first
        filtered.sort((x, y) => {
          const xd = x._scheduled_at_date ? x._scheduled_at_date.getTime() : 0;
          const yd = y._scheduled_at_date ? y._scheduled_at_date.getTime() : 0;
          return yd - xd;
        });
        setAppointments(filtered);
      }
    } catch (err) {
      console.error(err);
      let msg = "Failed to fetch appointments.";
      if (err?.response?.data?.detail) msg = err.response.data.detail;
      else if (err?.message) msg = err.message;
      setSnack({ open: true, message: msg, severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAppointments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const closeSnack = () => setSnack((s) => ({ ...s, open: false }));

  // helpers
  const formatDateTime = (iso) => {
    if (!iso) return "-";
    try {
      const d = new Date(iso);
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      const hh = String(d.getHours()).padStart(2, "0");
      const mm = String(d.getMinutes()).padStart(2, "0");
      return `${y}-${m}-${day} ${hh}:${mm}`;
    } catch {
      return iso;
    }
  };

  const openImageInNewTab = (path) => {
    const url = path?.startsWith("http") ? path : `${BASE_URL}${path}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  // Upload dialog functions
  const openUploadDialog = (appointment) => {
    setUploadAppointment(appointment);
    // default to first service if available
    const firstService = (appointment.appointment_services && appointment.appointment_services[0]) || null;
    setUploadServiceId(firstService ? firstService.id : "");
    setUploadIsBefore("true");
    setUploadFile(null);
    setUploadOpen(true);
  };

  const closeUploadDialog = () => {
    setUploadOpen(false);
    setUploadAppointment(null);
    setUploadServiceId("");
    setUploadFile(null);
    setUploadIsBefore("true");
    setUploadLoading(false);
  };

  const handleFileChange = (e) => {
    const f = e.target.files?.[0] ?? null;
    setUploadFile(f);
  };

  const submitUpload = async () => {
    if (!uploadAppointment || !uploadServiceId) {
      setSnack({ open: true, message: "Please select an appointment service to upload for", severity: "error" });
      return;
    }
    if (!uploadFile) {
      setSnack({ open: true, message: "Please select an image file to upload", severity: "error" });
      return;
    }

    setUploadLoading(true);
    try {
      const form = new FormData();
      form.append("image", uploadFile);
      form.append("is_before", uploadIsBefore === "true" ? "true" : "false");
      form.append("uploaded_by", "customer");

      // API: POST /appointment-services/{id}/upload-image/
      await ApiServices.post(`service-appointment-items/${uploadServiceId}/upload-image/`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      setSnack({ open: true, message: "Image uploaded successfully", severity: "success" });
      closeUploadDialog();
      await fetchAppointments(); // refresh to show new image
    } catch (err) {
      console.error(err);
      let msg = "Failed to upload image.";
      if (err?.response?.data) {
        const d = err.response.data;
        if (typeof d === "string") msg = d;
        else if (d?.detail) msg = d.detail;
        else msg = JSON.stringify(d);
      } else if (err?.message) msg = err.message;
      setSnack({ open: true, message: msg, severity: "error" });
    } finally {
      setUploadLoading(false);
    }
  };

  // Mark appointment completed
  const markCompleted = async (appointment) => {
    if (!appointment?.id) return;
    const ok = window.confirm("Mark this appointment as completed?"); // small confirmation
    if (!ok) return;
    try {
      setLoading(true);
      await ApiServices.post(`service-appointments/${appointment.id}/mark-completed/`, {});
      setSnack({ open: true, message: "Appointment marked completed", severity: "success" });
      await fetchAppointments();
    } catch (err) {
      console.error(err);
      let msg = "Failed to mark completed.";
      if (err?.response?.data?.detail) msg = err.response.data.detail;
      else if (err?.message) msg = err.message;
      setSnack({ open: true, message: msg, severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  // status -> chip color mapping
  const statusToChipProps = (status) => {
    const s = (status ?? "").toString().toUpperCase();
    switch (s) {
      case "COMPLETED":
        return { label: "Completed", color: "success" };
      case "NEW":
        return { label: "New", color: "warning" };
      case "PENDING":
        return { label: "Pending", color: "warning" };
      case "ASSIGNED_EMPLOYEE":
      case "ASSIGNED":
        return { label: "Assigned", color: "info" };
      case "CANCELLED":
      case "CANCELED":
        return { label: "Cancelled", color: "error" };
      default:
        return { label: status ?? "Unknown", color: "default" };
    }
  };

  const paymentStatusToChipProps = (pStatus) => {
    const s = (pStatus ?? "").toString().toUpperCase();
    switch (s) {
      case "PAID":
        return { label: "Paid", color: "success" };
      case "PENDING":
        return { label: "Pending", color: "warning" };
      case "FAILED":
        return { label: "Failed", color: "error" };
      default:
        return { label: pStatus ?? "Unknown", color: "default" };
    }
  };

  return (
    <Box sx={{ p: 3, width: "100%", boxSizing: "border-box", minHeight: "70vh", background: "#fff" }}>
      <Typography variant="h5" sx={{ color: "#094c9f", fontWeight: 700, mb: 2 }}>
        My Appointments
      </Typography>

      <Paper sx={{ width: "100%", overflow: "hidden" }} elevation={2}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer sx={{ maxHeight: "70vh", overflowX: "auto" }}>
            <Table stickyHeader sx={{ minWidth: 900, tableLayout: "fixed" }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, width: 60 }}>#</TableCell>
                  <TableCell sx={{ fontWeight: 700, width: 140 }}>Scheduled</TableCell>
                  <TableCell sx={{ fontWeight: 700, width: 120 }}>Status</TableCell>
                  {/* <TableCell sx={{ fontWeight: 700 }}>Business</TableCell> */}
                  <TableCell sx={{ fontWeight: 700 }}>Employee</TableCell>
                  <TableCell sx={{ fontWeight: 700, width: 180 }}>Services</TableCell>
                  <TableCell sx={{ fontWeight: 700, width: 110 }}>Total</TableCell>
                  <TableCell sx={{ fontWeight: 700, width: 120 }}>Payment</TableCell>
                  <TableCell sx={{ fontWeight: 700, width: 200 }}>Images</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, width: 160 }}>
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {appointments.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} align="center">
                      No appointments found.
                    </TableCell>
                  </TableRow>
                ) : (
                  appointments.map((a, idx) => {
                    // gather images across appointment_services
                    const services = Array.isArray(a.appointment_services) ? a.appointment_services : [];
                    const images = [];
                    services.forEach((s) => {
                      if (Array.isArray(s.images) && s.images.length) {
                        s.images.forEach((im) => images.push({ ...im, appointment_service_id: s.id }));
                      }
                    });

                    const statusChip = statusToChipProps(a.status);
                    const paymentChip = paymentStatusToChipProps(a.payment_status);

                    const isCompleted = (a.status ?? "").toString().toUpperCase() === "COMPLETED";

                    return (
                      <TableRow key={a.id} hover>
                        <TableCell>{idx + 1}</TableCell>

                        <TableCell>
                          <Typography variant="body2">{formatDateTime(a.scheduled_at)}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {a.address ?? ""}
                          </Typography>
                        </TableCell>

                        <TableCell>
                          <Chip label={statusChip.label} color={statusChip.color} size="small" />
                        </TableCell>

                        {/* <TableCell>
                          {a.business ? (
                            <>
                              <Typography sx={{ fontWeight: 700 }}>{a.business.name}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {a.business.email} • {a.business.phone}
                              </Typography>
                            </>
                          ) : (
                            <Typography color="text.secondary">Not assigned yet</Typography>
                          )}
                        </TableCell> */}

                        <TableCell>
                          {a.employee ? (
                            <>
                              <Typography sx={{ fontWeight: 700 }}>{`${a.employee.first_name} ${a.employee.last_name}`}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {a.employee.email} • {a.employee.phone}
                              </Typography>
                            </>
                          ) : (
                            <Typography color="text.secondary">Not assigned</Typography>
                          )}
                        </TableCell>

                        <TableCell>
                          {services.map((s) => (
                            <Box key={s.id} sx={{ mb: 0.8 }}>
                              <Typography sx={{ fontWeight: 700, fontSize: 13 }}>
                                Service #{s.service} — ₹{parseFloat(s.price_snapshot ?? 0).toFixed(2)}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Status: {s.status}
                              </Typography>
                              {/* Provide upload button per service row for convenience */}
                              <Box sx={{ mt: 0.5 }}>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={<UploadFileIcon />}
                                  onClick={() => {
                                    setUploadAppointment(a);
                                    setUploadServiceId(s.id);
                                    setUploadIsBefore("true");
                                    setUploadFile(null);
                                    setUploadOpen(true);
                                  }}
                                  sx={{ textTransform: "none" }}
                                >
                                  Upload image
                                </Button>
                              </Box>
                            </Box>
                          ))}
                        </TableCell>

                        <TableCell>
                          <Typography sx={{ fontWeight: 700 }}>{parseFloat(a.total_price ?? 0).toFixed(2)}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            Admin cost: {a.admin_cost_to_business ?? "-"}
                          </Typography>
                        </TableCell>

                        <TableCell>
                          <Typography sx={{ fontWeight: 700 }}>{/* show amount again if you want */}₹{parseFloat(a.total_price ?? 0).toFixed(2)}</Typography>
                          <Box sx={{ mt: 0.5 }}>
                            <Chip label={paymentChip.label} color={paymentChip.color} size="small" />
                          </Box>
                          {a.notes ? <Typography variant="caption" color="text.secondary">{a.notes.slice(0, 80)}</Typography> : null}
                        </TableCell>

                        <TableCell>
                          <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
                            {images.length === 0 ? (
                              <Typography variant="caption" color="text.secondary">
                                No images
                              </Typography>
                            ) : (
                              images.map((im) => {
                                const url = im.image?.startsWith("http") ? im.image : `${BASE_URL}${im.image}`;
                                const label = im.is_before ? "Before" : "After";
                                return (
                                  <Tooltip key={im.id} title={`${label} • ${im.uploaded_by}`}>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                      <img
                                        src={url}
                                        alt={label}
                                        style={{ width: 80, height: 60, objectFit: "cover", borderRadius: 6, cursor: "pointer", border: "1px solid rgba(0,0,0,0.08)" }}
                                        onClick={() => openImageInNewTab(im.image)}
                                      />
                                      <IconButton size="small" onClick={() => openImageInNewTab(im.image)} aria-label="open">
                                        <OpenInNewIcon fontSize="small" />
                                      </IconButton>
                                    </Box>
                                  </Tooltip>
                                );
                              })
                            )}
                          </Box>
                        </TableCell>

                        <TableCell align="right">
                          <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={() => openUploadDialog(a)}
                              startIcon={<UploadFileIcon />}
                              sx={{ textTransform: "none" }}
                            >
                              Upload
                            </Button>

                            {/* Hide/disable Mark Completed when already completed */}
                            {!isCompleted ? (
                              <Button
                                variant="contained"
                                size="small"
                                onClick={() => markCompleted(a)}
                                sx={{ backgroundColor: "#094c9f", textTransform: "none" }}
                              >
                                Mark Completed
                              </Button>
                            ) : (
                              <Button variant="contained" size="small" disabled sx={{ textTransform: "none", backgroundColor: "rgba(0,0,0,0.08)" }}>
                                Completed
                              </Button>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Upload Dialog (generic) */}
      <Dialog open={uploadOpen} onClose={closeUploadDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Upload image for appointment #{uploadAppointment?.id ?? ""}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
            <FormControl fullWidth size="small">
              <InputLabel id="service-select-label">Appointment service</InputLabel>
              <Select
                labelId="service-select-label"
                value={uploadServiceId}
                label="Appointment service"
                onChange={(e) => setUploadServiceId(e.target.value)}
              >
                {(uploadAppointment?.appointment_services ?? []).map((s) => (
                  <MenuItem key={s.id} value={s.id}>
                    {`Service #${s.service} — ₹${parseFloat(s.price_snapshot ?? 0).toFixed(2)}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                Image file
              </Typography>
              <input type="file" accept="image/*" onChange={handleFileChange} />
            </Box>

            <Box>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                Type
              </Typography>
              <RadioGroup row value={uploadIsBefore} onChange={(e) => setUploadIsBefore(e.target.value)}>
                <FormControlLabel value="true" control={<Radio />} label="Before" />
                <FormControlLabel value="false" control={<Radio />} label="After" />
              </RadioGroup>
            </Box>

            <Typography variant="caption" color="text.secondary">
              Uploaded images will be visible to business and employees. File size should be reasonable.
            </Typography>
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={closeUploadDialog} disabled={uploadLoading}>
            Cancel
          </Button>
          <Button onClick={submitUpload} variant="contained" disabled={uploadLoading} sx={{ backgroundColor: "#094c9f" }}>
            {uploadLoading ? <CircularProgress size={18} /> : "Upload"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snack.open} autoHideDuration={4500} onClose={closeSnack}>
        <Alert onClose={closeSnack} severity={snack.severity} sx={{ width: "100%" }}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CustomerAppointments;
